<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="wrapper" :class="`blog-${chrome.language}`">
    <h1
      ref="titleRef"
      class="m-d split-title blog__title"
      v-html="useLineBreak(content.hero.title)"
    ></h1>
    <div class="blog__filter__decoration"></div>
    <div
      ref="filterRef"
      class="blog__filter"
      :class="{ 'is-sticky': isSticky }"
    >
      <!-- <span class="info-label blog__filter__title">categories</span> -->
      <div class="blog__filter__items">
        <button
          v-for="(tag, index) in content.categories"
          :key="tag.label"
          :class="{
            'is-active': currentCat ? currentCat === tag.value : index === 0,
          }"
          class="blog__filter__item tag"
          @click.prevent="fetchCategories(tag.value)"
        >
          {{ tag.label }} <sup class="info-label">{{ tag.number }}</sup>
        </button>
      </div>
    </div>
    <div class="blog__layout row">
      <div ref="gridRef" class="grid pb-d m-d">
        <div
          v-for="(item, i) in articles"
          :key="item.title + uuidv4()"
          :data-index="i"
          data-blog-article
          class="grid-item"
          :class="[
            {
              'is-highlighted': (item as JournalArticleHighlighted)
                .isHighlighted,
            },
            articles.length <= 1 ? 'is-highlighted' : 'not-highlighted',
          ]"
        >
          <ArticleCard
            :key="item.title + uuidv4()"
            :content="item"
            class="blog__list__item"
          />
        </div>
      </div>
    </div>
    <hr />
    <Cta :content="content.cta" class="m-d" />
  </div>
</template>

<script setup lang="ts">
import { useAxios } from '@monofront/vue-axios-plugin'
import { gsap } from 'gsap'
import isMobile from 'ismobilejs'
import { nextTick, onMounted, ref, computed, PropType } from 'vue'
import { v4 as uuidv4 } from 'uuid' // Vlitjs requires a unique id for each video

import { useLineBreak } from '@/utils/filters'
import { createSplitTitle, SplitTitle } from '@/animations/splitTitle'

import ArticleCard from '@/components/ArticleCard.vue'
import Cta from '@/components/ui/Cta.vue'

import { useChromeStore } from '@/stores/chrome'

const axios = useAxios()
const chrome = useChromeStore()

import {
  Content,
  Article,
  JournalArticle,
  JournalArticleHighlighted,
} from '@/types/views/journal'
import { getApiUrl } from '@/config/app'

const props = defineProps({
  content: {
    type: Object as PropType<Content>,
    required: true,
  },
})

const titleRef = ref<HTMLElement>()
const filterRef = ref<HTMLElement>()
const currentCat = ref('all')
const gridRef = ref<HTMLElement>()
const isSticky = ref(false)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let Masonry: any | undefined
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let masonry: any | undefined

const articlesData = ref<JournalArticle[]>(props.content.articles)
const highlightedData = ref<JournalArticleHighlighted>(
  props.content.highlighted as JournalArticleHighlighted
)

highlightedData.value.isHighlighted = true

const hideCards = () => {
  const cards = document.querySelectorAll('[data-blog-article]')

  return gsap
    .to(cards, {
      duration: 0.5,
      yPercent: '-=10',
      opacity: 0,
      stagger: 0.1,
      ease: 'power3.out',
    })
    .then()
}

const showCards = () => {
  const cards = document.querySelectorAll('[ data-blog-article]')

  return gsap
    .from(cards, {
      duration: 0.5,
      yPercent: '+=10',
      opacity: 0,
      stagger: 0.1,
      ease: 'power3.out',
    })
    .then()
}

const fetchCategories = async (category: string) => {
  let url = `${getApiUrl()}/pages/journal`

  const categoryUrl = `?category=${category}`
  const urlCategory = window.location.href.split('?')

  if (category === 'all') {
    history.pushState('url', '', urlCategory[0])
  } else {
    url += categoryUrl
    history.pushState('url', '', categoryUrl)
  }

  const { data } = await axios.get(url)

  await hideCards()

  currentCat.value = category
  articlesData.value = data.content.articles || []

  nextTick(() => {
    masonryGrid()
  })
}

const articles = computed<Article[]>(() => {
  const results: Article[] = []

  if (
    currentCat.value === 'all' ||
    highlightedData.value.categories.find(c => c.value === currentCat.value)
  ) {
    results.push(highlightedData.value)
  }

  return results.concat(articlesData.value)
})

let splitTitle: SplitTitle

const masonryGrid = () => {
  if (!Masonry || !gridRef.value) {
    return
  }

  masonry?.destroy()

  masonry = new Masonry(gridRef.value, {
    // options
    itemSelector: '.grid-item',
    columnWidth: 1,
    resize: true,
    transitionDuration: '0.2s',
  })

  showCards()
}

onMounted(async () => {
  if (!titleRef.value) {
    return
  }

  const urlParams = new URLSearchParams(window.location.search).get('category')

  if (urlParams) {
    currentCat.value = urlParams || 'all'
    fetchCategories(currentCat.value)
  }

  if (!isMobile().phone) {
    splitTitle = createSplitTitle(titleRef.value)
    splitTitle.intro.play()
  }

  Masonry = (await import('masonry-layout')).default

  masonryGrid()

  window.addEventListener('resize', () => {
    masonry?.reloadItems()
  })
})
</script>

<style lang="scss" scoped>
$bp: l;

.blog__title {
  padding-bottom: 0;
  text-transform: uppercase;

  @include mq($until: s) {
    font-size: 12vw;

    .blog-fr & {
      font-size: 11vw;
    }
  }
}

.blog__list__item {
  display: block;
}

:deep(.split-title .split-line:first-of-type) {
  margin-left: auto;
}

.blog__filter {
  @include scrollbars(2px, $foreground-color: var(--c-foreground));

  position: relative;
  z-index: 40;
  width: 100%;
  height: auto;
  padding: 1em 0;
  background-color: var(--c-background);
  border: 1px solid var(--c-foreground);
  border-width: 1px 0;

  @include mq($until: s) {
    overflow: scroll;
  }

  @include mq(l) {
    position: sticky;
    top: 15px;

    &::before {
      position: absolute;
      z-index: -1;
      bottom: 101%;
      display: block;
      width: 100%;
      height: 40px;
      background-color: var(--c-background);
      content: '';
    }
  }
}

.blog__filter__decoration {
  @include mq(l) {
    position: sticky;
    z-index: 41;
    top: 15px;
    display: block;
    width: 100%;
    height: 2px;
    // prettier-ignore
    background: linear-gradient(180deg, rgba(238, 248, 230, 100%) 50%, rgba(238, 248, 230, 0%) 100%);
    opacity: 1;
    content: '';
  }
}

.blog__filter__items {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}

.blog__filter__title {
  // prettier-ignore
  @include fluid(margin-bottom,(xxs: 10px, xxl: 20px));

  display: block;

  .is-sticky & {
    opacity: 0;
  }
}

.blog__filter__item {
  flex-shrink: 0;
  margin-right: 3px;
  padding: 0.25em 0.9em;
  color: var(--c-foreground);
  font-size: 1.4rem;
  background: transparent;
  border-color: transparent;
  transition: border-color 0.35s ease-in-out;

  &.is-active {
    border-color: var(--c-foreground);
  }

  &:hover {
    cursor: pointer;

    &:not(&.is-active) {
      border-color: transparent;
      filter: brightness(1.5);
    }
  }

  @include mq($until: $bp) {
    &:not(:last-child) {
      margin-right: 1.5rem;
    }
  }
}

.grid {
  width: 100%;
  margin-bottom: 8rem;
  transition: all 0.4s ease-in;
  will-change: height;
  transform-origin: center top;

  @include mq(s) {
    transform: translateX(0) scale(1.035);
  }

  @include mq($until: l) {
    /* stylelint-disable-next-line declaration-no-important */
    padding-bottom: 0 !important;
  }

  @include mq(l) {
    transform: translateX(0) scale(1.034);
  }

  @include mq(wrapper) {
    transform: translateX(5.1px) scale(1.044);
  }

  @include mq(xl) {
    transform: translateX(5.7px) scale(1.042);
  }

  @include mq(xxl) {
    transform: translateX(6.2px) scale(1.038);
  }
}

.grid-item {
  width: 100%;

  &.is-highlighted {
    @include mq(s) {
      width: 100%;
    }

    @include mq(wrapper) {
      width: 66%;
    }
  }

  @include mq($until: s) {
    &:not(:last-child) {
      margin-bottom: 40px;
    }
  }

  @include mq(s) {
    padding: 16px;
  }

  @include mq(s) {
    width: 50%;
  }

  @include mq(wrapper) {
    width: 33%;
  }
}
</style>
